/**
 * One place that turns a thrown failure into the sentence the founder needs.
 *
 * It exists because the distinction `client.ts` draws was being collapsed at
 * the point of use. `TodayPage` rendered "DaVinci is not available locally"
 * for *any* query error — and the most likely error in this system is not a
 * dead server, it is a `401` after the server restarted, because sessions live
 * in memory by design (`session.ts`). Telling the founder to check that the
 * server is running, when the actual repair is to re-open the launch link,
 * makes the Retry button a lie in exactly the case it must not be.
 *
 * So the mapping is written once, here, and both the route error boundary and
 * any data-fetching screen use it. Three outcomes, three different repairs:
 *
 * - **No server.** Start it, then retry.
 * - **No session.** Sign in again (#371; before accounts the repair was to
 *   re-open the launch URL, which is now the founder's alternative to signing
 *   in). Retry cannot help, so no Retry. The API client has usually already
 *   sent the tab to `/login` by the time this renders; this is what a mounted
 *   screen shows in the moment before, and in any context with no redirect.
 * - **The server refused.** Retry may help; the code is shown for the log.
 *
 * The session branch tests `status`, not `code`, and that is deliberate now
 * that both are available (#14). A code is only present when the body parsed
 * as a contract envelope; the status is on the response either way. Choosing
 * the always-present signal for the branch that decides whether to offer
 * Retry means a malformed body degrades the *detail line* rather than the
 * advice — the person still gets "sign in again" instead of the generic
 * sentence, which is the one case where being wrong wastes real time.
 */

import { ApiRequestError, LocalServerUnavailable } from '../api/client.js';
import { useMessages } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import { LocalUnavailable } from './local-unavailable.js';

const messages = defineMessages({
  en: {
    endedTitle: 'This session has ended',
    endedBody:
      'DaVinci is running, but this browser tab is no longer signed in — which happens when you sign out, when a session reaches its end, and whenever the server restarts, because sessions are deliberately never written to disk.',
    signInAgain: 'Sign in again',
    signInAgainAfter: 'and this page will work.',
    refusedTitle: 'DaVinci could not complete that',
    refusedBody:
      'The server answered, but refused this request. Retrying may work; if it does not, the local server log has the detail.',
    noEnvelope: 'no error envelope',
    retry: 'Retry',
    faultTitle: 'Something in DaVinci went wrong',
    faultBody:
      'This is a fault in the application itself, not in anything you did. Reloading the page is safe — nothing you have recorded is stored in this browser.',
  },
  he: {
    endedTitle: 'ההתחברות הסתיימה',
    endedBody:
      'DaVinci פועל, אבל הלשונית הזו כבר לא מחוברת — זה קורה אחרי התנתקות, כשחיבור מגיע לסופו, ובכל הפעלה מחדש של השרת, כי חיבורים לא נשמרים בכוונה בדיסק.',
    signInAgain: 'התחברות מחדש',
    signInAgainAfter: 'והדף הזה יעבוד.',
    refusedTitle: 'DaVinci לא הצליח להשלים את הבקשה',
    refusedBody:
      'השרת ענה אך סירב לבקשה. ייתכן שניסיון נוסף יצליח. אם לא, הפרטים נמצאים ביומן השרת המקומי.',
    noEnvelope: 'אין פרטי שגיאה',
    retry: 'נסה שוב',
    faultTitle: 'משהו ב-DaVinci השתבש',
    faultBody:
      'זו תקלה באפליקציה עצמה ולא במשהו שעשית. אפשר לטעון את הדף מחדש בבטחה — שום דבר ממה שתיעדת לא נשמר בדפדפן הזה.',
  },
});

export interface FailureNoticeProps {
  readonly error: unknown;
  /** Omitted where the caller has nothing to re-run. */
  readonly onRetry?: () => void;
}

export function FailureNotice({ error, onRetry }: FailureNoticeProps): React.JSX.Element {
  const t = useMessages(messages);
  if (error instanceof LocalServerUnavailable) {
    return <LocalUnavailable {...(onRetry !== undefined ? { onRetry } : {})} />;
  }

  if (error instanceof ApiRequestError && error.status === 401) {
    return (
      <div className="notice notice-screen" role="alert">
        <h2>{t.endedTitle}</h2>
        <p>{t.endedBody}</p>
        <p>
          <a href="/login">{t.signInAgain}</a> {t.signInAgainAfter}
        </p>
      </div>
    );
  }

  if (error instanceof ApiRequestError) {
    return (
      <div className="notice notice-screen" role="alert">
        <h2>{t.refusedTitle}</h2>
        <p>{t.refusedBody}</p>
        <p className="notice-detail">
          {error.code ?? t.noEnvelope}
          {error.requestId !== undefined ? ` · ${error.requestId}` : null}
        </p>
        {onRetry !== undefined ? (
          <button type="button" onClick={onRetry}>
            {t.retry}
          </button>
        ) : null}
      </div>
    );
  }

  // Anything else is a fault in this frontend. The error itself is never
  // rendered: §9 keeps learner responses out of error payloads, and a
  // component that was rendering a response can carry one in its message.
  return (
    <div className="notice notice-screen" role="alert">
      <h2>{t.faultTitle}</h2>
      <p>{t.faultBody}</p>
    </div>
  );
}
